import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import {
  CmdSubpage,
  CmdSimpleSelect,
  CmdInput,
  CmdTextarea,
  CmdBoxButton,
  CmdSubpageButton,
  CmdLabel,
} from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import FormikValidatorService from '../../../Services/FormikValidatorService';
import { TABLE_OPERATING_COSTS } from '../../../Services/TableService';
import configJs from '../../../../config';
import PermissionsService, { PERMISSION_COST } from '../../../Services/PermissionsService';
import { showMessage } from '../../Toastr/ToastService';
import { IOption } from '../../Forms/CmdField';
import LoaderService from '../../../Services/LoaderService';
import usePageWithForm from '../../../hooks/usePageWithForm';
import OnlineHelp from '../../OnlineHelp';
import { IVehicles } from '../../Vehicles/types';
import CmdUploadHookForm from '../../Forms/CmdUploadHookForm';
import { IValidationApiResponse } from '../../../interfaces/apiResponse';
import SimpleSelectWithSearchHookForm from '../../Forms/Fields/SimpleSelectWithSearchHookForm';
import Calendar from '../../Forms/ReactHookFormFields/Calendar';
import { formatDateInSentence } from './OperationCostOverviewService';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import { selectedVehiclesAtom, vehiclesAtom } from '../../../store/recoil/vehicles';
import useAnalytics from '../../../hooks/useAnalytics';
import { getRequest, postRequest, putRequest } from '../../../Services/HttpService';

interface SubpageFormProps {
  closeCallback: () => void;
  selectedId: number | null;
  updateTable?: (() => void) | undefined;
  customerIds?: number | null;
}

const VAT_INDEX_ID = 'vat';
const ACCOUNTING_INDEX_ID = 'accountingTs';
const VEHICLE_INDEX_ID = 'vehicleId';
const NAME_INDEX_ID = 'name';
const TYPE_INDEX_ID = 'typeId';
const SUPPLIER_INDEX_ID = 'supplierId';
const PRICE_INDEX_ID = 'price';
const PRICE_VAT_INDEX_ID = 'priceIncVat';
const CURRENCY_INDEX_ID = 'currencyId';
const BILL_INDEX_ID = 'billId';
const NOTE_INDEX_ID = 'note';
const ATTACHMETS_INDEX_ID = 'attachments';
const SECONDARY_CURRENCY_INDEX_ID = 'secondaryCurrencyId';
const SECONDARY_VAT_INDEX_ID = 'secondaryVat';
const SECONDARY_PRICE_INDEX_ID = 'secondaryPrice';
const SECONDARY_PRICE_INC_VAT_INDEX_ID = 'secondaryPriceIncVat';

type TCostOverviewForm =
  | 'vat'
  | 'accountingTs'
  | 'vehicleId'
  | 'name'
  | 'typeId'
  | 'supplierId'
  | 'price'
  | 'priceIncVat'
  | 'currencyId'
  | 'billId'
  | 'note'
  | 'attachments'
  | 'secondaryCurrencyId'
  | 'secondaryVat'
  | 'secondaryPrice'
  | 'secondaryPriceIncVat';

interface ITypeIdOption {
  value: number;
  item: string;
  customerLabel: string | null;
  disabled?: boolean;
}
interface ISuplierIdOption {
  value: number;
  item: string;
}
interface ISuplierIdOption {
  value: number;
  item: string;
}

interface ICurrencyOption {
  currencyCode: string;
  currencyId: number;
  tax: number;
}

interface ISecondaryCurrencyOption {
  value: string;
  item: string;
}

interface ICostsOverviewFormValues {
  accountingTs: Date;
  vehicleId: string;
  name: string;
  typeId: string;
  supplierId: string;
  vat: string;
  price: string;
  priceIncVat: string;
  currencyId: string;
  billId: string;
  note: string;
  attachments: string[];
  secondaryCurrencyId: string;
  secondaryVat: string;
  secondaryPrice: string;
  secondaryPriceIncVat: string;
  identifier?: string;
  secondaryUnitPriceIncVat?: string;
  secondaryUnitPrice?: string;
  secondaryVatPrice?: string;
}

function CostsOverviewForm(props: SubpageFormProps): JSX.Element {
  usePageWithForm();
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const allVehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const [isSecondaryCurrency, setIsSecondaryCurrency] = React.useState<boolean>(false);
  const [vehicles, setVehicles] = React.useState<IOption[]>([]);
  const [typeIdOptions, setTypeIdOptions] = React.useState<ITypeIdOption[]>([]);
  const [supplierIdOptions, setSupplierIdOptions] = React.useState<ISuplierIdOption[]>([]);
  const [secondCurrencyOptions, setSecondCurrencyOptions] = React.useState<
    ISecondaryCurrencyOption[]
  >([]);

  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);

  let isAllowed = true;
  if (props.selectedId && props.customerIds) {
    isAllowed = PermissionsService.hasAccess(TABLE_OPERATING_COSTS, ['update'], props.customerIds);
  }

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<ICostsOverviewFormValues>({
    mode: 'onChange',
    defaultValues: {
      accountingTs: new Date(),
      vehicleId: '',
      name: '',
      typeId: '',
      supplierId: '',
      vat: '',
      price: '',
      priceIncVat: '',
      currencyId: '',
      billId: '',
      note: '',
      attachments: [],
      secondaryCurrencyId: '',
      secondaryVat: '',
      secondaryPrice: '',
      secondaryPriceIncVat: '',
    },
    resolver: async (data) => {
      let formValidation = { ...validation };
      if (!watch('secondaryCurrencyId') && !isSecondaryCurrency) {
        formValidation = {
          ...validation,
          secondaryVat: [],
          secondaryPriceIncVat: [],
          secondaryPrice: [],
        };
      }

      const validateErros = validateFields(data, formValidation);

      let formErrors = transformErrors(validateErros);

      if (formValidation && formValidation.accountingTs) {
        const accountingTs = new Date(data.accountingTs);
        const filteredDateValidation = formValidation.accountingTs.find(
          (item) => item.param === 'maxDate'
        );
        if (filteredDateValidation) {
          if (accountingTs > new Date()) {
            const formattedSentence = formatDateInSentence(filteredDateValidation.message);
            formErrors = {
              ...formErrors,
              accountingTs: { type: 'maxDate', message: formattedSentence },
            };
          }
        }
      }

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const tableName = TABLE_OPERATING_COSTS;
  const id = props.selectedId;

  const handleOnSubmit = async (values: ICostsOverviewFormValues) => {
    const newValues = {
      ...values,
      customerId: id ? props.customerIds : allVehicles[Number(values.vehicleId)].customerId,
    };
    if (!values.secondaryCurrencyId || !isSecondaryCurrency) {
      const keysToDelete = [
        'secondaryCurrencyId',
        'secondaryVat',
        'secondaryUnitPriceIncVat',
        'secondaryUnitPrice',
        'secondaryPrice',
        'secondaryVatPrice',
        'secondaryPriceIncVat',
      ] as const;

      keysToDelete.forEach((key) => {
        if (key in newValues) {
          delete newValues[key];
        }
      });
    }

    if (id) {
      const url = `/v1/${tableName}/${id}/edit?customerId=${props.customerIds}`;
      LoaderService.showLoader(true);
      newValues.identifier = '';

      const response: AxiosResponse | false = await putRequest(url, newValues, {
        returnAxiosResponse: true,
        showToaster: true,
      });

      if (
        (response as AxiosResponse).data &&
        (response as AxiosResponse).status === 200 &&
        props.updateTable
      ) {
        trackEvent(['Operation costs overview form', 'submit', 'Update']);
        props.closeCallback();
        props.updateTable();
      }

      LoaderService.showLoader(false);
    } else {
      const url = `/v1/${tableName}/add`;
      LoaderService.showLoader(true);

      const response: AxiosResponse | false = await postRequest(url, newValues, {
        returnAxiosResponse: true,
      });

      if (
        (response as AxiosResponse).data &&
        (response as AxiosResponse).status === 200 &&
        props.updateTable
      ) {
        trackEvent(['Operation costs overview form', 'submit', 'Save']);
        props.closeCallback();
        props.updateTable();
      }

      LoaderService.showLoader(false);
    }
  };

  const getVehicleIdSelect = async () => {
    const indexId = VEHICLE_INDEX_ID;
    const url = `/v1/${tableName}/form/${indexId}`;

    LoaderService.showLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if (id) {
      if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
        const options = (response as AxiosResponse).data;
        setVehicles(options);

        LoaderService.showLoader(false);

        return options;
      }
    } else if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
      const options = (response as AxiosResponse).data;

      LoaderService.showLoader(false);
      if (!selectedVehicles.length) {
        showMessage('', f({ id: 'reports.chooseInNavigator' }), 'warning');
        setValue('vehicleId', '');
        setValue('typeId', '');
        setValue('supplierId', '');
        setTypeIdOptions([]);
        setSupplierIdOptions([]);
        return [];
      }
      // if (!costsOverviewVehicleOptions) {
      //   return [];
      // }

      const filterVehicles = options.filter((item: IOption) =>
        selectedVehicles.includes(Number(item.value))
      );

      const filterVehiclesWithPermission = filterVehicles.filter((item: IOption) =>
        PermissionsService.hasAccess(
          PERMISSION_COST,
          ['create'],
          allVehicles[Number(item.value)]?.customerId
        )
      );

      if (selectedVehicles.length > 0 && filterVehiclesWithPermission.length === 0) {
        showMessage('', f({ id: 'costsOverviewForm.selectedVehicle.permission' }), 'warning');
      }

      return filterVehiclesWithPermission;
    }

    LoaderService.showLoader(false);
    return false;
  };

  const getCurrency = (cId: number, options: IOption[]) => {
    if (cId && options) {
      const option = options.find((i: IOption) => String(i.value) === String(cId));
      if (option) {
        return option.item;
      }
      return '';
    }
    return '';
  };

  const handleDataForCurrency = async (vehicleId: number) => {
    if (allVehicles[vehicleId]) {
      const url = `/v1/${tableName}/form/customerCurrency?customerId=${allVehicles[vehicleId].customerId}`;

      LoaderService.showLoader(true);

      const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

      if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
        const options = (response as AxiosResponse).data;
        if (options.currencyCode) {
          setValue(CURRENCY_INDEX_ID, options.currencyCode);
          setValue(VAT_INDEX_ID, options.tax);
        }

        LoaderService.showLoader(false);

        return options;
      }

      LoaderService.showLoader(false);
      return false;
    }
    return false;
  };

  const getDataForSelect = async (indexIdName: string, vehicleId?: number) => {
    let url = `/v1/${tableName}/form/${indexIdName}${id ? `?customerId=${props.customerIds}` : ''}`;

    if (vehicleId && allVehicles[vehicleId]) {
      url = `/v1/${tableName}/form/${indexIdName}?customerId=${allVehicles[vehicleId].customerId}`;

      if (!id) {
        handleDataForCurrency(vehicleId);
      }
    }
    if (id && indexIdName === TYPE_INDEX_ID && props.customerIds) {
      url = `/v1/${tableName}/${id}/form/${indexIdName}?customerId=${props.customerIds}`;
    }
    if (id && indexIdName === SUPPLIER_INDEX_ID && props.customerIds) {
      url = `/v1/${tableName}/${id}/form/${indexIdName}?customerId=${props.customerIds}`;
    }

    LoaderService.showLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if ((response as AxiosResponse) && (response as AxiosResponse).status === 200) {
      const options = (response as AxiosResponse).data;

      if (options.currencyCode) {
        setValue(CURRENCY_INDEX_ID, options.currencyCode);
        if (!id) {
          setValue(VAT_INDEX_ID, options.tax);
        }
      }
      if (indexIdName === CURRENCY_INDEX_ID) {
        setSecondCurrencyOptions(options);
      }
      if (indexIdName === TYPE_INDEX_ID) {
        const filter = options.filter((item: ITypeIdOption) => !item.disabled);
        setTypeIdOptions(filter);
      }
      if (indexIdName === SUPPLIER_INDEX_ID) {
        setSupplierIdOptions(options);
      }

      LoaderService.showLoader(false);
      return options;
    }

    LoaderService.showLoader(false);
    return false;
  };

  const getSupplierIdSelect = (vehicleId?: number) => {
    const indexId = SUPPLIER_INDEX_ID;
    if (vehicleId) {
      getDataForSelect(indexId, vehicleId);
    } else {
      getDataForSelect(indexId);
    }
  };

  const getData = async () => {
    const url = `/v1/${tableName}/${id}${
      props.customerIds ? `?customerId=${props.customerIds}` : ''
    }`;
    LoaderService.showLoader(true);

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
      const fields = (response as AxiosResponse).data.cost;

      Object.keys(fields).forEach((fieldName) => {
        if (fieldName === SECONDARY_CURRENCY_INDEX_ID && fields[fieldName]) {
          setIsSecondaryCurrency(true);
        }
        if (fieldName === VAT_INDEX_ID) {
          const vat =
            typeof fields[fieldName] === 'string'
              ? fields[fieldName].replace(',', '.')
              : fields[fieldName];

          setValue(fieldName as TCostOverviewForm, vat);
        } else {
          setValue(fieldName as TCostOverviewForm, fields[fieldName]);
        }
      });

      getSupplierIdSelect(fields.vehicleId);

      getVehicleIdSelect();
      LoaderService.showLoader(false);
      return fields;
    }

    LoaderService.showLoader(false);
    return false;
  };

  const handleSecondaryCurrency = () => {
    const willBeSecondary = !isSecondaryCurrency;
    if (!willBeSecondary) {
      setValue('secondaryCurrencyId', '');
      setValue('secondaryVat', '');
      setValue('secondaryPriceIncVat', '');
    }
    setIsSecondaryCurrency(willBeSecondary);
  };

  const getValidation = async (indexId?: string) => {
    let url = `/v1/${tableName}/validation${id ? `?customerId=${props.customerIds}` : ''}`;
    if (id && indexId) {
      url = `/v1/${tableName}/${id}/validation/${indexId}${
        id ? `?customerId=${props.customerIds}` : ''
      }`;
    }
    LoaderService.showLoader(true);

    dayjs().format('DD.MM.YYYY HH:mm');

    const response: AxiosResponse | false = await getRequest(url, { returnAxiosResponse: true });

    if ((response as AxiosResponse).data && (response as AxiosResponse).status === 200) {
      const fields = (response as AxiosResponse).data;

      setValidation((oldState) => ({ ...oldState, ...fields }));
    }

    LoaderService.showLoader(false);
  };

  const secondaryCurrencyButton: CmdBoxButton[] = [
    {
      id: '',
      className: '',
      type: 'button',
      title: f({ id: 'form.sections.refuelings.addOriginalPrice' }),
      buttonCallback: handleSecondaryCurrency,
    },
  ];

  const getTypeSelect = (vehicleId?: number) => {
    if (vehicleId && allVehicles[vehicleId]) {
      getDataForSelect(TYPE_INDEX_ID, vehicleId);
    } else {
      getDataForSelect(TYPE_INDEX_ID);
    }
  };

  const getCurrencyIdSelect = () => {
    getDataForSelect('customerCurrency');
  };

  const getSecondaryCurrencyIdSelect = () => {
    getDataForSelect(CURRENCY_INDEX_ID);
  };

  const handleOnChangeVehicleId = async (value: string) => {
    trackEvent(['Operation costs overview form', 'change', 'Vehicle', Number(value)]);
    setValue('vehicleId', value as string);
    trigger('vehicleId');
    if (selectedVehicles && value) {
      LoaderService.showLoader(true);

      getTypeSelect(Number(value));
      getSupplierIdSelect(Number(value));

      LoaderService.showLoader(false);
    }
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['costsOverviewFormData'],
        queryFn: () => getData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!props.selectedId,
      },
      {
        queryKey: ['costsOverviewFormDataVehicleSelect', !id && selectedVehicles.length],
        queryFn: () => getVehicleIdSelect(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!allVehicles,
      },
      {
        queryKey: ['costsOverviewFormDataTypeSelect', id],
        queryFn: () => getTypeSelect(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['costsOverviewFormDataCurrencySelect'],
        queryFn: () => getCurrencyIdSelect(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: ['costsOverviewSecondaryCurrencyOptions'],
        queryFn: () => getSecondaryCurrencyIdSelect(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isSecondaryCurrency,
      },
      {
        queryKey: ['costsOverviewValidation'],
        queryFn: () => getValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isAllowed,
      },
      {
        queryKey: ['costsOverviewValidationTypeId'],
        queryFn: () => getValidation(TYPE_INDEX_ID),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isAllowed && !!id,
      },
      {
        queryKey: ['costsOverviewValidationSupplierId'],
        queryFn: () => getValidation(SUPPLIER_INDEX_ID),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: isAllowed && !!id,
      },
    ],
  });

  const { isLoading: isLoadingCostOverviewData } = results[0];
  const { data: costsOverviewVehicleOptions, isLoading: isLoadingcostsOverviewVehicleOptions } =
    results[1];
  const { isLoading: isLoadingcostsOverviewTypeIdOptions } = results[2];
  const { isLoading: isLoadingcostsCurrencyOptions } = results[3];
  const { isLoading: isLoadingSecondaryCurencyOptions } = results[4];
  const { isLoading: isLoadingCostValidation } = results[5];
  const { isLoading: isLoadingCostTypeValidation } = results[6];
  const { isLoading: isLoadingCostSupplierValidation } = results[7];

  let vehicleIdName = '';
  let typeIdName = '';
  let supplierIdName = '';
  let currencyIdName = '';

  if (id) {
    if (vehicles) {
      const filter = vehicles.find(
        (items: IOption) => String(items.value) === String(watch('vehicleId'))
      );
      if (filter) {
        vehicleIdName = filter.item;
      }
    }

    if (typeIdOptions) {
      const filter = typeIdOptions.find(
        (items: ITypeIdOption) => String(items.value) === String(watch('typeId'))
      );
      if (filter) {
        typeIdName = filter.item;
      }
    }

    if (supplierIdOptions) {
      const filter = supplierIdOptions.find(
        (items: ISuplierIdOption) => String(items.value) === String(watch('supplierId'))
      );
      if (filter) {
        supplierIdName = filter.item;
      }
    }

    if (secondCurrencyOptions) {
      const filter = secondCurrencyOptions.find(
        (items: IOption) => String(items.value) === String(watch('secondaryCurrencyId'))
      );
      if (filter) {
        currencyIdName = filter.item;
      }
    }
  }

  const subpageButtons: CmdSubpageButton[] = [
    {
      id: 'form-subpage-costs-overview-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'suppliersForm.close' }),
      closeCallback: () => {
        trackEvent(['Operation costs overview form', 'click', 'Close form']);
        props.closeCallback();
      },
    },
    {
      id: 'form-subpage-costs-overview-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'suppliersForm.save' }),
      submitCallback: handleSubmit(handleOnSubmit),
    },
  ];

  const subpageButtonsReadOnly: CmdSubpageButton[] = [subpageButtons[0]];

  const handleCurrencyChange = (isSecondary?: boolean) => {
    const priceIncVatField = isSecondary ? 'secondaryPriceIncVat' : 'priceIncVat';
    const vatField = isSecondary ? 'secondaryVat' : 'vat';
    const priceField = isSecondary ? SECONDARY_PRICE_INDEX_ID : PRICE_INDEX_ID;

    const priceIncVatValue = Number(watch(priceIncVatField)) || 0;

    let vatValue = watch(vatField);
    if (typeof vatValue === 'string') {
      vatValue = vatValue.replace(',', '.');
    }

    let vatPercentage = Number(vatValue) / 100;

    if (Number.isNaN(vatPercentage)) {
      vatPercentage = 0;
    }

    const unitPriceValue = Math.round((100 * priceIncVatValue) / (vatPercentage + 1)) / 100;

    setValue(vatField, vatValue || '');
    setValue(priceField, unitPriceValue.toString());
  };

  const handleOptions = (options: ITypeIdOption[] | ISuplierIdOption[]) => {
    if (!selectedVehicles.length) {
      return [];
    }
    return options;
  };

  const handleIntroOption = () => {
    if (selectedVehicles.length > 0 && supplierIdOptions.length === 0 && watch('vehicleId')) {
      return f({ id: 'costsOverviewForm.supplier.emptyOptions' });
    }
    if (id || watch('vehicleId')) {
      return f({ id: 'suppliersForm.choose' });
    }

    if (!selectedVehicles.length) {
      return f({ id: 'costsOverviewForm.chooseFirstVehicle' });
    }

    return f({ id: 'costsOverviewForm.chooseFirstVehicle' });
  };

  const isLoading =
    isLoadingCostOverviewData ||
    isLoadingcostsOverviewVehicleOptions ||
    isLoadingcostsCurrencyOptions ||
    isLoadingcostsOverviewTypeIdOptions ||
    isLoadingSecondaryCurencyOptions ||
    isLoadingcostsOverviewTypeIdOptions ||
    isLoadingCostValidation ||
    isLoadingCostTypeValidation ||
    isLoadingCostSupplierValidation;

  return (
    <CmdSubpage
      id="costs-overview-form"
      title={f({ id: 'menu.costsOverview' })}
      subtitle={id ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
      buttons={isAllowed ? subpageButtons : subpageButtonsReadOnly}
      maintitle={f({ id: 'menu.costs' })}
      isNavigatorEnabled={true}
      onlineHelp={<OnlineHelp topic="costs" />}
      loader={isLoading}
    >
      {isAllowed ? (
        <div>
          <div className="row">
            <div className="col-xxl-6">
              <CmdBox
                id="cost-basic-information"
                title={f({ id: 'costsOverviewForm.firstSectionTitle' })}
              >
                <Controller
                  name="accountingTs"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      label={f({ id: 'costsOverviewForm.dateTime' })}
                      id={ACCOUNTING_INDEX_ID}
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.accountingTs ? validation.accountingTs : []
                      )}
                      afterToday={true}
                      onChangeCallback={(value) => {
                        setValue('accountingTs', value as Date);
                        trigger('accountingTs');
                      }}
                      error={errors.accountingTs?.message as string}
                      touched={true}
                    />
                  )}
                />

                {id && vehicleIdName ? (
                  <CmdLabel
                    name="vehicleId"
                    id={VEHICLE_INDEX_ID}
                    title={f({ id: 'costsOverviewForm.vehicle' })}
                    value={vehicleIdName}
                    areLabels={true}
                    // newStyle={true}
                  />
                ) : (
                  <Controller
                    name="vehicleId"
                    control={control}
                    render={({ field }) => (
                      <SimpleSelectWithSearchHookForm
                        {...field}
                        id={VEHICLE_INDEX_ID}
                        label={f({ id: 'costsOverviewForm.vehicle' })}
                        required={FormikValidatorService.isFieldRequired(
                          validation && validation.vehicleId ? validation.vehicleId : []
                        )}
                        introOption={f({ id: 'suppliersForm.choose' })}
                        options={costsOverviewVehicleOptions || []}
                        onChangeCallback={handleOnChangeVehicleId}
                        control={control}
                        canRemoveValue={true}
                      />
                    )}
                  />
                )}

                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'costsOverviewForm.costsName' })}
                      id={NAME_INDEX_ID}
                      type="text"
                      placeholder=""
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.name ? validation.name : []
                      )}
                      onChangeCallback={(value) => {
                        setValue('name', value);
                        trigger('name');
                      }}
                      error={errors.name?.message}
                      touched={true}
                    />
                  )}
                />
                {watch('vehicleId') ? (
                  <Controller
                    name="typeId"
                    control={control}
                    render={({ field }) => (
                      <SimpleSelectWithSearchHookForm
                        {...field}
                        id={TYPE_INDEX_ID}
                        label={f({ id: 'costsOverviewForm.costsType' })}
                        introOption={
                          id || watch('vehicleId')
                            ? f({ id: 'suppliersForm.choose' })
                            : f({ id: 'costsOverviewForm.chooseFirstVehicle' })
                        }
                        required={FormikValidatorService.isFieldRequired(
                          validation && validation.typeId ? validation.typeId : []
                        )}
                        options={handleOptions(typeIdOptions)}
                        onChangeCallback={(value) => {
                          trackEvent([
                            'Operation costs overview form',
                            'change',
                            'Type',
                            Number(value),
                          ]);
                          setValue('typeId', value);
                          trigger('typeId');
                        }}
                        control={control}
                        canRemoveValue={true}
                        disabled={!watch('vehicleId')}
                      />
                    )}
                  />
                ) : (
                  <CmdLabel
                    id="typeId"
                    title={f({ id: 'costsOverviewForm.costsType' })}
                    value={f({ id: 'form.placeholders.corrections.selectVehicleFirst' })}
                    areLabels={true}
                  />
                )}

                {watch('vehicleId') ? (
                  <Controller
                    name="supplierId"
                    control={control}
                    render={({ field }) => (
                      <SimpleSelectWithSearchHookForm
                        {...field}
                        id={SUPPLIER_INDEX_ID}
                        label={f({ id: 'costsOverviewForm.supplier' })}
                        introOption={handleIntroOption()}
                        required={FormikValidatorService.isFieldRequired(
                          validation && validation.supplierId ? validation.supplierId : []
                        )}
                        options={handleOptions(supplierIdOptions)}
                        onChangeCallback={(value) => {
                          trackEvent([
                            'Operation costs overview form',
                            'change',
                            'Supplier',
                            Number(value),
                          ]);
                          setValue('supplierId', value);
                          trigger('supplierId');
                        }}
                        control={control}
                        canRemoveValue={true}
                      />
                    )}
                  />
                ) : (
                  <CmdLabel
                    id="typeId"
                    title={f({ id: 'costsOverviewForm.supplier' })}
                    value={f({ id: 'form.placeholders.corrections.selectVehicleFirst' })}
                    areLabels={true}
                  />
                )}
              </CmdBox>

              <CmdBox id="cost-third" title={f({ id: 'costsOverviewForm.thirdSectionTitle' })}>
                <Controller
                  name="billId"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'costsOverviewForm.billId' })}
                      id={BILL_INDEX_ID}
                      type="text"
                      placeholder=""
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.billId ? validation.billId : []
                      )}
                      onChangeCallback={(value) => {
                        setValue('billId', value);
                        trigger('billId');
                      }}
                      error={errors.billId?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => (
                    <CmdTextarea
                      {...field}
                      id={NOTE_INDEX_ID}
                      label={f({ id: 'costsOverviewForm.note' })}
                      placeholder=""
                      setValue={(value) => {
                        setValue('note', value);
                        trigger('note');
                      }}
                      value={field.value as string}
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.note ? validation.note : []
                      )}
                      error={errors.note?.message}
                      touched={true}
                    />
                  )}
                />
                {watch('vehicleId') ? (
                  <Controller
                    name="attachments"
                    control={control}
                    render={({ field }) => (
                      <CmdUploadHookForm
                        {...field}
                        id={ATTACHMETS_INDEX_ID}
                        label={f({ id: 'costsOverviewForm.attachments' })}
                        required={false}
                        number={3}
                        maxFileSize={3}
                        acceptedFileTypes={['bmp', 'png', 'jpg', 'jpeg', 'gif', 'pdf']}
                        dnd={true}
                        url={`${configJs.Backend.url}/api/v1/file/upload${`?customerId=${
                          id
                            ? props.customerIds
                            : allVehicles[Number(watch('vehicleId'))].customerId
                        }`}`}
                        control={control}
                        onChangeCallback={(value) => {
                          setValue('attachments', value);
                          trigger('attachments');
                        }}
                      />
                    )}
                  />
                ) : (
                  <CmdLabel
                    id="cost-upload-read"
                    title={f({ id: 'costsOverviewForm.attachments' })}
                    value={f({ id: 'form.placeholders.corrections.selectVehicleFirst' })}
                    areLabels={true}
                  />
                )}
              </CmdBox>
            </div>
            <div className="col-xxl-6">
              <CmdBox
                id="cost-second"
                title={f({ id: 'costsOverviewForm.secondSectionTitle' })}
                buttons={isSecondaryCurrency ? [] : secondaryCurrencyButton}
              >
                <CmdLabel
                  id="currency"
                  title={f({ id: 'costsOverviewForm.currency' })}
                  value={watch('currencyId')}
                  areLabels={true}
                />
                <Controller
                  name="vat"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'costsOverviewForm.vat' })}
                      id={VAT_INDEX_ID}
                      type="number"
                      placeholder=""
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.vat ? validation.vat : []
                      )}
                      currency={watch('currencyId')}
                      onChangeCallback={(value) => {
                        setValue('vat', value);
                        trigger('vat');
                        handleCurrencyChange();
                        if (watch('price') && Number(watch('price')) > 0) {
                          clearErrors('price');
                        }
                      }}
                      error={errors.vat?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="priceIncVat"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'costsOverviewForm.priceIncVat' })}
                      id={PRICE_VAT_INDEX_ID}
                      type="number"
                      placeholder=""
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.priceIncVat ? validation.priceIncVat : []
                      )}
                      currency={watch('currencyId')}
                      onChangeCallback={(value) => {
                        setValue('priceIncVat', value);
                        trigger('priceIncVat');
                        handleCurrencyChange();
                        if (watch('price') && Number(watch('price')) > 0) {
                          clearErrors('price');
                        }
                      }}
                      error={errors.priceIncVat?.message}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="price"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      label={f({ id: 'costsOverviewForm.price' })}
                      id={PRICE_INDEX_ID}
                      type="number"
                      placeholder=""
                      required={FormikValidatorService.isFieldRequired(
                        validation && validation.price ? validation.price : []
                      )}
                      currency={watch('currencyId')}
                      onChangeCallback={(value) => {
                        setValue('price', value);
                        trigger('price');
                        if (typeof value === 'number' && value > 0) {
                          clearErrors('price');
                        }
                      }}
                      error={errors.price?.message}
                      touched={true}
                    />
                  )}
                />

                {isSecondaryCurrency && (
                  <>
                    <hr
                      style={{
                        marginRight: '-16px',
                        marginLeft: '-16px',
                        paddingTop: '10px',
                        marginBottom: '3px',
                        borderTop: '1px solid #ddd',
                      }}
                    />
                    <div
                      style={{ cursor: 'pointer', marginBottom: '12px' }}
                      className="text-right form-group"
                    >
                      <span onClick={handleSecondaryCurrency} data-cy="remove-section-icon">
                        <i
                          style={{ color: '#737373' }}
                          className="cmd-icons cmd-icon-bin"
                          aria-hidden="true"
                        />
                        &nbsp;{f({ id: 'table.filter.remove' })}
                      </span>
                    </div>

                    <Controller
                      name="secondaryCurrencyId"
                      control={control}
                      render={({ field }) => (
                        <CmdSimpleSelect
                          {...field}
                          id={SECONDARY_CURRENCY_INDEX_ID}
                          label={f({ id: 'costsOverviewForm.currency' })}
                          introOption={f({ id: 'suppliersForm.choose' })}
                          options={secondCurrencyOptions || []}
                          onChangeCallback={(value) => {
                            setValue('secondaryCurrencyId', value);
                            trackEvent([
                              'Operation costs overview form',
                              'change',
                              'Secondary currency',
                              Number(value),
                            ]);

                            if (watch('secondaryCurrencyId')) {
                              const rulesSecondaryVat = validation?.secondaryVat || [];
                              rulesSecondaryVat.push({
                                param: 'required',
                                value: true,
                                message: f({ id: 'form.rules.inputIsRequired' }),
                              });
                              const rulesSecondaryPriceIncVat =
                                validation?.secondaryPriceIncVat || [];
                              rulesSecondaryPriceIncVat.push({
                                param: 'required',
                                value: true,
                                message: f({ id: 'form.rules.inputIsRequired' }),
                              });
                              setValidation((oldState) => ({
                                ...oldState,
                                secondaryVat: rulesSecondaryVat,
                                secondaryPriceIncVat: rulesSecondaryPriceIncVat,
                              }));
                            }
                            trigger('secondaryCurrencyId');
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="secondaryVat"
                      control={control}
                      render={({ field }) => (
                        <CmdInput
                          {...field}
                          label={f({ id: 'costsOverviewForm.vat' })}
                          id={SECONDARY_VAT_INDEX_ID}
                          type="number"
                          placeholder=""
                          required={!!watch('secondaryCurrencyId')}
                          currency={getCurrency(
                            Number(watch('secondaryCurrencyId')),
                            secondCurrencyOptions
                          )}
                          disabled={!watch('secondaryCurrencyId')}
                          onChangeCallback={(value) => {
                            setValue('secondaryVat', value);
                            trigger('secondaryVat');
                            handleCurrencyChange(true);
                            if (watch('secondaryPrice') && Number(watch('secondaryPrice')) > 0) {
                              clearErrors('secondaryPrice');
                            }
                          }}
                          error={errors.secondaryVat?.message}
                          touched={true}
                        />
                      )}
                    />
                    <Controller
                      name="secondaryPriceIncVat"
                      control={control}
                      render={({ field }) => (
                        <CmdInput
                          {...field}
                          label={f({ id: 'costsOverviewForm.priceIncVat' })}
                          id={SECONDARY_PRICE_INC_VAT_INDEX_ID}
                          type="number"
                          placeholder=""
                          required={!!watch('secondaryCurrencyId')}
                          currency={getCurrency(
                            Number(watch('secondaryCurrencyId')),
                            secondCurrencyOptions
                          )}
                          disabled={!watch('secondaryCurrencyId')}
                          onChangeCallback={(value) => {
                            setValue('secondaryPriceIncVat', value);
                            trigger('secondaryPriceIncVat');
                            handleCurrencyChange(true);
                            if (watch('secondaryPrice') && Number(watch('secondaryPrice')) > 0) {
                              clearErrors('secondaryPrice');
                            }
                          }}
                          error={errors.secondaryPriceIncVat?.message}
                          touched={true}
                        />
                      )}
                    />
                    <Controller
                      name="secondaryPrice"
                      control={control}
                      render={({ field }) => (
                        <CmdInput
                          {...field}
                          label={f({ id: 'costsOverviewForm.price' })}
                          id={SECONDARY_PRICE_INDEX_ID}
                          type="number"
                          placeholder=""
                          currency={getCurrency(
                            Number(watch('secondaryCurrencyId')),
                            secondCurrencyOptions
                          )}
                          disabled={!watch('secondaryCurrencyId')}
                          onChangeCallback={(value) => {
                            setValue('secondaryPrice', value);
                            trigger('secondaryPrice');
                            if (typeof value === 'number' && value > 0) {
                              clearErrors('secondaryPrice');
                            }
                          }}
                          error={errors.secondaryPrice?.message}
                          touched={true}
                        />
                      )}
                    />
                  </>
                )}
              </CmdBox>
            </div>
          </div>
        </div>
      ) : (
        <div className="form-horizontal">
          <div className="row">
            <div className="col-xxl-6">
              <CmdBox
                id="costsOverviewForm.firstSectionTitle"
                title={f({ id: 'costsOverviewForm.firstSectionTitle' })}
              >
                <CmdLabel
                  id="cost-date"
                  title={f({ id: 'costsOverviewForm.dateTime' })}
                  value={dayjs(watch('accountingTs')).format('DD.MM.YYYY HH:mm')}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="cost-vehicle"
                  title={f({ id: 'costsOverviewForm.vehicle' })}
                  value={vehicleIdName}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="cost-name"
                  title={f({ id: 'costsOverviewForm.costsName' })}
                  value={watch('name')}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="cost-type"
                  title={f({ id: 'costsOverviewForm.costsType' })}
                  value={typeIdName}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="cost-supplier-name"
                  title={f({ id: 'costsOverviewForm.supplier' })}
                  value={supplierIdName}
                  areLabels={true}
                  // isWithSubpage={true}
                />
              </CmdBox>

              <CmdBox
                id="costsOverviewForm.thirdSectionTitle"
                title={f({ id: 'costsOverviewForm.thirdSectionTitle' })}
              >
                <CmdLabel
                  id="cost-id"
                  title={f({ id: 'costsOverviewForm.billId' })}
                  value={watch('billId')}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="cost-note"
                  title={f({ id: 'costsOverviewForm.note' })}
                  value={watch('note')}
                  areLabels={true}
                  // isWithSubpage={true}
                />

                {watch('vehicleId') ? (
                  <Controller
                    name="attachments"
                    control={control}
                    render={({ field }) => (
                      <CmdUploadHookForm
                        {...field}
                        id={ATTACHMETS_INDEX_ID}
                        label={f({ id: 'costsOverviewForm.attachments' })}
                        // required={FormikValidatorService.isFieldRequired(
                        //   validation && validation.attachments ? validation.attachments : []
                        // )}
                        number={3}
                        maxFileSize={3}
                        acceptedFileTypes={['bmp', 'png', 'jpg', 'jpeg', 'gif', 'pdf']}
                        readonly={true}
                        url={`${configJs.Backend.url}/api/v1/file/upload${`?customerId=${
                          id
                            ? props.customerIds
                            : allVehicles[Number(watch('vehicleId'))].customerId
                        }`}`}
                        control={control}
                        onChangeCallback={(value) => {
                          setValue('attachments', value);
                          trigger('attachments');
                        }}
                        // width="col-9"
                      />
                    )}
                  />
                ) : (
                  <CmdLabel
                    id="cost-upload-readOnly"
                    title={f({ id: 'costsOverviewForm.attachments' })}
                    value={f({ id: 'form.placeholders.corrections.selectVehicleFirst' })}
                    // newStyle={true}
                    // isWithSubpage={true}
                  />
                )}
              </CmdBox>
            </div>

            <div className="col-xxl-6">
              <CmdBox
                id="costsOverviewForm.secondSectionTitle"
                title={f({ id: 'costsOverviewForm.secondSectionTitle' })}
              >
                <CmdLabel
                  id="currency-id"
                  title={f({ id: 'costsOverviewForm.currency' })}
                  value={watch('currencyId')}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="vat"
                  title={f({ id: 'costsOverviewForm.vat' })}
                  value={watch('vat')}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="priceIncVat"
                  title={f({ id: 'costsOverviewForm.priceIncVat' })}
                  value={watch('priceIncVat')}
                  areLabels={true}
                  // isWithSubpage={true}
                />
                <CmdLabel
                  id="price"
                  title={f({ id: 'costsOverviewForm.price' })}
                  value={watch('price') ? String(watch('price')) : ''}
                  areLabels={true}
                  // isWithSubpage={true}
                />

                {isSecondaryCurrency && (
                  <>
                    <hr
                      style={{
                        marginRight: '-16px',
                        marginLeft: '-16px',
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        borderTop: '1px solid #ddd',
                      }}
                    />
                    <CmdLabel
                      id="secondary-id"
                      title={f({ id: 'costsOverviewForm.currency' })}
                      value={currencyIdName}
                      areLabels={true}
                      // isWithSubpage={true}
                      // newStyle={true}
                    />
                    <CmdLabel
                      id="secondary-vat"
                      title={f({ id: 'costsOverviewForm.vat' })}
                      value={watch('secondaryVat')}
                      // isWithSubpage={true}
                      // newStyle={true}
                      areLabels={true}
                    />
                    <CmdLabel
                      id="secondary-priceIncVat"
                      value={watch('secondaryPriceIncVat')}
                      title={f({ id: 'costsOverviewForm.price' })}
                      // isWithSubpage={true}
                      // newStyle={true}
                      areLabels={true}
                    />
                    <CmdLabel
                      id="secondary-price"
                      value={watch('secondaryPrice')}
                      title={f({ id: 'costsOverviewForm.price' })}
                      // isWithSubpage={true}
                      // newStyle={true}
                      areLabels={true}
                    />
                  </>
                )}
              </CmdBox>
            </div>
          </div>
        </div>
      )}
    </CmdSubpage>
  );
}

export default CostsOverviewForm;
